// Admin API client for Bearer token authentication
import type {
  HRLoginRequest,
  HRLoginResponse,
  HRSessionResponse,
  AuthError,
} from "../types/auth";

import type {
  Company,
  CompanyQueryParams,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  ApiResponse,
  HRUser,
  User,
  UserQueryParams,
  CreateHRUserRequest,
  UpdateUserRequest,
  ResetPasswordRequest,
  UserRole,
  UsersApiResponse,
  Application,
  ApplicationQueryParams,
  ApplicationsResponse,
  UpdateApplicationRequest,
  PaginationMeta,
} from "./api";

// Re-export types needed by other components
export type {
  Company,
  CompanyQueryParams,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  ApiResponse,
  HRUser,
  User,
  UserQueryParams,
  CreateHRUserRequest,
  UpdateUserRequest,
  ResetPasswordRequest,
  UserRole,
  UsersApiResponse,
  Application,
  ApplicationQueryParams,
  ApplicationsResponse,
  UpdateApplicationRequest,
  PaginationMeta,
} from "./api";

// Base URL configuration
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

// Admin API client class - uses Bearer token authentication
class AdminApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    token: string
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Always use Bearer token for admin API
    const config: RequestInit = {
      ...options,
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers,
      },
    };

    // Don't set Content-Type for FormData, let browser handle it
    const isFormData = options.body instanceof FormData;
    if (isFormData) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { "Content-Type": _, ...otherHeaders } = config.headers as Record<string, string>;
      config.headers = otherHeaders;
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Handle HTTP errors
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // Ignore JSON parsing errors for error responses
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Re-throw with more user-friendly message for network errors
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error - please check your connection and try again"
        );
      }

      throw error;
    }
  }

  // HR Authentication API
  async hrLogin(data: HRLoginRequest): Promise<HRLoginResponse | AuthError> {
    return fetch(`${this.baseUrl}/api/v1/admin/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // Ignore JSON parsing errors
        }
        throw new Error(errorMessage);
      }
      return response.json();
    });
  }

  async hrGetSession(token: string): Promise<HRSessionResponse | AuthError> {
    return this.request<HRSessionResponse>("/api/v1/admin/auth/session", {}, token);
  }

  async hrLogout(token: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      "/api/v1/admin/auth/logout",
      { method: "POST" },
      token
    );
  }

  // Companies API (admin operations)
  async getCompanies(
    params: CompanyQueryParams | undefined,
    token: string
  ): Promise<ApiResponse<{ companies: Company[]; pagination: PaginationMeta }>> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append("status", params.status);
    if (params?.search) searchParams.append("search", params.search);
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.sortBy) searchParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) searchParams.append("sortOrder", params.sortOrder);
    if (params?.startDate) searchParams.append("startDate", params.startDate);
    if (params?.endDate) searchParams.append("endDate", params.endDate);

    const queryString = searchParams.toString();
    const url = `/api/v1/admin/companies${queryString ? `?${queryString}` : ""}`;

    return this.request<ApiResponse<{ companies: Company[]; pagination: PaginationMeta }>>(
      url, {}, token
    );
  }

  async getCompany(
    id: string,
    token: string
  ): Promise<ApiResponse<{ company: Company }>> {
    return this.request<ApiResponse<{ company: Company }>>(
      `/api/v1/admin/companies/${id}`, {}, token
    );
  }

  async createCompany(
    data: CreateCompanyRequest,
    token: string
  ): Promise<ApiResponse<{ company: Company; message: string }>> {
    return this.request<ApiResponse<{ company: Company; message: string }>>(
      "/api/v1/admin/companies",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
      token
    );
  }

  async updateCompany(
    id: string,
    data: UpdateCompanyRequest,
    token: string
  ): Promise<ApiResponse<{ company: Company }>> {
    return this.request<ApiResponse<{ company: Company }>>(
      `/api/v1/admin/companies/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
      token
    );
  }

  async deleteCompany(
    id: string,
    token: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(
      `/api/v1/admin/companies/${id}`,
      { method: "DELETE" },
      token
    );
  }

  async uploadCompanyLogo(
    id: string,
    file: File,
    token: string
  ): Promise<ApiResponse<{ logoUrl: string; logoR2Key: string }>> {
    const formData = new FormData();
    formData.append("logo", file);

    return this.request<ApiResponse<{ logoUrl: string; logoR2Key: string }>>(
      `/api/v1/admin/companies/${id}/logo`,
      {
        method: "POST",
        body: formData,
      },
      token
    );
  }

  // Users API methods
  async getUsers(
    params: UserQueryParams | undefined,
    token: string
  ): Promise<UsersApiResponse> {
    const searchParams = new URLSearchParams();

    if (params) {
      if (params.page) searchParams.set("page", params.page.toString());
      if (params.limit) searchParams.set("limit", params.limit.toString());
      if (params.search) searchParams.set("search", params.search);
      if (params.role) searchParams.set("role", params.role);
      if (params.status) searchParams.set("status", params.status);
      if (params.userType) searchParams.set("userType", params.userType);
      if (params.companyId) searchParams.set("companyId", params.companyId);
    }

    const url = `/api/v1/admin/users${
      searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`;

    return this.request<UsersApiResponse>(url, {}, token);
  }

  async getUserById(
    id: string,
    token: string
  ): Promise<ApiResponse<{ data: User }>> {
    return this.request<ApiResponse<{ data: User }>>(
      `/api/v1/admin/users/${id}`, {}, token
    );
  }

  async createHRUser(
    data: CreateHRUserRequest,
    token: string
  ): Promise<ApiResponse<{ data: HRUser; message: string }>> {
    return this.request<ApiResponse<{ data: HRUser; message: string }>>(
      "/api/v1/admin/users/hr",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
      token
    );
  }

  async updateUser(
    id: string,
    data: UpdateUserRequest,
    token: string
  ): Promise<ApiResponse<{ data: User; message: string }>> {
    return this.request<ApiResponse<{ data: User; message: string }>>(
      `/api/v1/admin/users/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
      token
    );
  }

  async deleteUser(
    id: string,
    token: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(
      `/api/v1/admin/users/${id}`,
      { method: "DELETE" },
      token
    );
  }

  async resetUserPassword(
    id: string,
    data: ResetPasswordRequest,
    token: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(
      `/api/v1/admin/users/${id}/reset-password`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
      token
    );
  }

  async getUserRoles(token: string): Promise<ApiResponse<{ data: UserRole[] }>> {
    return this.request<ApiResponse<{ data: UserRole[] }>>(
      "/api/v1/admin/users/roles", {}, token
    );
  }

  // Applications API methods
  async getApplications(
    params: ApplicationQueryParams | undefined,
    token: string
  ): Promise<ApiResponse<ApplicationsResponse>> {
    const searchParams = new URLSearchParams();

    if (params) {
      if (params.page) searchParams.set("page", params.page.toString());
      if (params.limit) searchParams.set("limit", params.limit.toString());
      if (params.search) searchParams.set("search", params.search);
      if (params.status) searchParams.set("status", params.status);
      if (params.sortField) searchParams.set("sortField", params.sortField);
      if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
      if (params.startDate) searchParams.set("startDate", params.startDate);
      if (params.endDate) searchParams.set("endDate", params.endDate);
    }

    const url = `/api/v1/admin/applications${
      searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`;

    return this.request<ApiResponse<ApplicationsResponse>>(url, {}, token);
  }

  async getApplication(
    id: string,
    token: string
  ): Promise<ApiResponse<Application>> {
    return this.request<ApiResponse<Application>>(
      `/api/v1/admin/applications/${id}`, {}, token
    );
  }

  async updateApplication(
    id: string,
    data: UpdateApplicationRequest,
    token: string
  ): Promise<ApiResponse<Application>> {
    return this.request<ApiResponse<Application>>(
      `/api/v1/admin/applications/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
      token
    );
  }

  async deleteApplication(
    id: string,
    token: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(
      `/api/v1/admin/applications/${id}`,
      { method: "DELETE" },
      token
    );
  }

  // Permission Management API methods

  async getPermissions(
    token: string,
    params?: {
      page?: string;
      limit?: string;
      search?: string;
      category?: string;
      systemOnly?: string;
    }
  ): Promise<ApiResponse<{
    permissions: Array<{
      id: string;
      name: string;
      description: string;
      category: string;
      isSystemPermission: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page);
    if (params?.limit) searchParams.append("limit", params.limit);
    if (params?.search) searchParams.append("search", params.search);
    if (params?.category) searchParams.append("category", params.category);
    if (params?.systemOnly) searchParams.append("systemOnly", params.systemOnly);

    const queryString = searchParams.toString();
    const url = `/api/v1/admin/permissions${queryString ? `?${queryString}` : ""}`;

    return this.request(url, {}, token);
  }

  async getPermissionMatrix(token: string): Promise<ApiResponse<{
    permissions: Array<any>;
    roles: Array<{
      name: string;
      permissions: string[];
    }>;
  }>> {
    return this.request("/api/v1/admin/permissions/matrix", {}, token);
  }

  async getRolePermissionsFromPermissionsApi(
    role: string,
    token: string
  ): Promise<ApiResponse<{
    role: string;
    permissions: Array<any>;
  }>> {
    return this.request(`/api/v1/admin/permissions/roles/${role}`, {}, token);
  }

  async updateRolePermissionsFromPermissionsApi(
    role: string,
    data: { permissions: string[] },
    token: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request(
      `/api/v1/admin/permissions/roles/${role}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
      token
    );
  }

  async getUserPermissionsDetailed(
    userId: string,
    token: string
  ): Promise<ApiResponse<{
    userId: string;
    permissions: any;
    effectivePermissions: string[];
  }>> {
    return this.request(`/api/v1/admin/permissions/users/${userId}`, {}, token);
  }

  async grantUserPermission(
    userId: string,
    data: { permissionId: string; expiresAt?: string; reason?: string },
    token: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request(
      `/api/v1/admin/permissions/users/${userId}/grant`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
      token
    );
  }

  async revokeUserPermission(
    userId: string,
    data: { permissionId: string; reason?: string },
    token: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request(
      `/api/v1/admin/permissions/users/${userId}/revoke`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
      token
    );
  }

  async removeUserPermissionOverride(
    userId: string,
    permissionId: string,
    token: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request(
      `/api/v1/admin/permissions/users/${userId}/${permissionId}`,
      { method: "DELETE" },
      token
    );
  }

  async cleanupExpiredPermissions(
    token: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request(
      "/api/v1/admin/permissions/cleanup",
      { method: "POST" },
      token
    );
  }

  async getPermissionAudit(
    params: { userId?: string; limit?: number; offset?: number },
    token: string
  ): Promise<ApiResponse<{
    auditLog: Array<any>;
    pagination: {
      total: number;
      offset: number;
      limit: number;
      hasMore: boolean;
    };
  }>> {
    const searchParams = new URLSearchParams();
    if (params.userId) searchParams.append("userId", params.userId);
    if (params.limit !== undefined)
      searchParams.append("limit", params.limit.toString());
    if (params.offset !== undefined)
      searchParams.append("offset", params.offset.toString());

    const queryString = searchParams.toString();
    const url = `/api/v1/admin/permissions/audit${queryString ? `?${queryString}` : ""}`;

    return this.request(url, {}, token);
  }

  // Role Management API methods

  async getRoles(token: string): Promise<ApiResponse<Array<{
    role: string;
    description: string;
    level: number;
    permissionCount: number;
    userCount: number;
  }>>> {
    return this.request("/api/v1/admin/roles", {}, token);
  }

  async getRolePermissions(
    role: string,
    token: string,
    params?: { page?: string; limit?: string; category?: string }
  ): Promise<ApiResponse<{
    data: Array<any>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page);
    if (params?.limit) searchParams.append("limit", params.limit);
    if (params?.category) searchParams.append("category", params.category);

    const queryString = searchParams.toString();
    const url = `/api/v1/admin/roles/${role}/permissions${queryString ? `?${queryString}` : ""}`;

    return this.request(url, {}, token);
  }

  async syncRolePermissions(
    role: string,
    data: { permissions: string[]; reason?: string },
    token: string
  ): Promise<ApiResponse<{
    added: string[];
    removed: string[];
    errors: Array<any>;
    message: string;
  }>> {
    return this.request(
      `/api/v1/admin/roles/${role}/permissions`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
      token
    );
  }

  async removeRolePermission(
    role: string,
    permissionId: string,
    reason?: string,
    token?: string
  ): Promise<ApiResponse<{ message: string }>> {
    const searchParams = new URLSearchParams();
    if (reason) searchParams.append("reason", reason);

    const queryString = searchParams.toString();
    const url = `/api/v1/admin/roles/${role}/permissions/${permissionId}${queryString ? `?${queryString}` : ""}`;

    return this.request(url, { method: "DELETE" }, token!);
  }

  async getRoleMatrix(token: string): Promise<ApiResponse<Array<{
    role: string;
    permissions: Array<any>;
  }>>> {
    return this.request("/api/v1/admin/roles/matrix", {}, token);
  }

  async getRoleStatistics(
    role: string,
    token: string
  ): Promise<ApiResponse<{
    userCount: number;
    permissionCount: number;
    systemPermissionCount: number;
    recentChanges: number;
    lastModified?: string;
  }>> {
    return this.request(`/api/v1/admin/roles/${role}/statistics`, {}, token);
  }

  // Legacy: Keep for backward compatibility
  async getUserPermissions(userId: string, token: string): Promise<{
    granted: string[];
    denied: string[];
  }> {
    return this.request<{ granted: string[]; denied: string[] }>(
      `/api/v1/admin/users/${userId}/permissions`, {}, token
    );
  }

  // Generic HTTP methods for other APIs
  async get<T>(endpoint: string, token: string): Promise<T> {
    return this.request<T>(endpoint, {}, token);
  }

  async post<T>(endpoint: string, data: any, token: string): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      },
      token
    );
  }

  async put<T>(endpoint: string, data: any, token: string): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "PUT",
        body: data ? JSON.stringify(data) : undefined,
      },
      token
    );
  }

  async delete<T>(endpoint: string, token: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" }, token);
  }
}

// Export singleton instance
export const adminApi = new AdminApiClient(API_BASE_URL);

// Export convenience functions for admin operations
export const adminAuthApi = {
  login: (data: HRLoginRequest) => adminApi.hrLogin(data),
  getSession: (token: string) => adminApi.hrGetSession(token),
  logout: (token: string) => adminApi.hrLogout(token),
};

export const adminCompaniesApi = {
  getAll: (params: CompanyQueryParams | undefined, token: string) =>
    adminApi.getCompanies(params, token),
  getById: (id: string, token: string) => adminApi.getCompany(id, token),
  create: (data: CreateCompanyRequest, token: string) =>
    adminApi.createCompany(data, token),
  update: (id: string, data: UpdateCompanyRequest, token: string) =>
    adminApi.updateCompany(id, data, token),
  delete: (id: string, token: string) => adminApi.deleteCompany(id, token),
  uploadLogo: (id: string, file: File, token: string) =>
    adminApi.uploadCompanyLogo(id, file, token),
};

export const adminUsersApi = {
  getAll: (params: UserQueryParams | undefined, token: string) =>
    adminApi.getUsers(params, token),
  getById: (id: string, token: string) => adminApi.getUserById(id, token),
  createHR: (data: CreateHRUserRequest, token: string) =>
    adminApi.createHRUser(data, token),
  update: (id: string, data: UpdateUserRequest, token: string) =>
    adminApi.updateUser(id, data, token),
  delete: (id: string, token: string) => adminApi.deleteUser(id, token),
  resetPassword: (id: string, data: ResetPasswordRequest, token: string) =>
    adminApi.resetUserPassword(id, data, token),
  getRoles: (token: string) => adminApi.getUserRoles(token),
};

export const adminApplicationsApi = {
  getAll: (params: ApplicationQueryParams | undefined, token: string) =>
    adminApi.getApplications(params, token),
  getById: (id: string, token: string) => adminApi.getApplication(id, token),
  update: (id: string, data: UpdateApplicationRequest, token: string) =>
    adminApi.updateApplication(id, data, token),
  delete: (id: string, token: string) => adminApi.deleteApplication(id, token),
};

export const adminPermissionsApi = {
  getAll: (token: string, params?: Parameters<typeof adminApi.getPermissions>[1]) =>
    adminApi.getPermissions(token, params),
  getMatrix: (token: string) => adminApi.getPermissionMatrix(token),
  getRolePermissions: (role: string, token: string) =>
    adminApi.getRolePermissionsFromPermissionsApi(role, token),
  updateRolePermissions: (role: string, data: { permissions: string[] }, token: string) =>
    adminApi.updateRolePermissionsFromPermissionsApi(role, data, token),
  getUserPermissions: (userId: string, token: string) =>
    adminApi.getUserPermissionsDetailed(userId, token),
  grantUserPermission: (
    userId: string,
    data: { permissionId: string; expiresAt?: string; reason?: string },
    token: string
  ) => adminApi.grantUserPermission(userId, data, token),
  revokeUserPermission: (
    userId: string,
    data: { permissionId: string; reason?: string },
    token: string
  ) => adminApi.revokeUserPermission(userId, data, token),
  removeUserPermissionOverride: (userId: string, permissionId: string, token: string) =>
    adminApi.removeUserPermissionOverride(userId, permissionId, token),
  cleanupExpired: (token: string) => adminApi.cleanupExpiredPermissions(token),
  getAudit: (params: Parameters<typeof adminApi.getPermissionAudit>[0], token: string) =>
    adminApi.getPermissionAudit(params, token),
};

export const adminRolesApi = {
  getAll: (token: string) => adminApi.getRoles(token),
  getPermissions: (
    role: string,
    token: string,
    params?: Parameters<typeof adminApi.getRolePermissions>[2]
  ) => adminApi.getRolePermissions(role, token, params),
  syncPermissions: (
    role: string,
    data: { permissions: string[]; reason?: string },
    token: string
  ) => adminApi.syncRolePermissions(role, data, token),
  removePermission: (role: string, permissionId: string, reason: string | undefined, token: string) =>
    adminApi.removeRolePermission(role, permissionId, reason, token),
  getMatrix: (token: string) => adminApi.getRoleMatrix(token),
  getStatistics: (role: string, token: string) => adminApi.getRoleStatistics(role, token),
};